import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import { T, PageHeader, Badge } from '../components/UI';
import { confirmDelete } from '../utils/confirmDelete';
import { useAuth } from '../context/AuthContext';
import SendInvoiceModal from '../components/SendInvoiceModal';

const TODAY = new Date(new Date().getTime()+8*60*60*1000).toISOString().split('T')[0];

const STATUS_MAP = {
  pending:   { label:'Pending',   bg:'var(--amber-bg)',   color:'var(--amber)'  },
  ready:     { label:'Ready',     bg:'var(--green-bg)',   color:'var(--green)'  },
  collected: { label:'Collected', bg:'var(--bg)',         color:'var(--muted)'  },
  paid:      { label:'Paid',      bg:'var(--green)',      color:'#fff'          },
};

export default function WalkInList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [invoiceFor, setInvoiceFor] = useState(null);   // the walk-in whose invoice dialog is open
  const [walkIns,  setWalkIns]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [date,     setDate]     = useState(location.state?.date || TODAY);   // coming back from the edit form keeps the same day
  const [editingStatus, setEditingStatus] = useState(null);
  const [bookings, setBookings] = useState([]);   // customers who booked on the website for this day

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/walkins?date=${date}`);
      setWalkIns(data.walkIns || []);
    } catch {}
    finally { setLoading(false); }
  };

  // Online bookings for the day (read from the website's database). Best-effort: if it can't be reached the sheet still works.
  const fetchBookings = async () => {
    try {
      const { data } = await api.get('/walkins/online', { params: { date, location: user?.selectedLocation || '' } });
      setBookings(data.bookings || []);
    } catch { setBookings([]); }
  };

  useEffect(() => { fetch(); fetchBookings(); }, [date]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/walkins/${id}`, { status });
      setWalkIns(prev => prev.map(w => w._id===id ? data.walkIn : w));
    } catch {}
    setEditingStatus(null);
  };

  const handleDelete = async (id) => {
    if (!await confirmDelete('This walk-in record will be permanently removed.')) return;
    try {
      await api.delete(`/walkins/${id}`);
      setWalkIns(prev => prev.filter(w => w._id !== id));
    } catch {}
  };

  const totalRevenue = walkIns.reduce((a,w)=>a+(w.price||0), 0);
  const paidTotal    = walkIns.filter(w=>w.status==='paid').reduce((a,w)=>a+(w.price||0), 0);

  // After an invoice is sent, update just that row (the invoice badge and the remembered email)
  const invoiceSent = (id) => (invoice, email) => setWalkIns(prev => prev.map(w => w._id===id ? { ...w, invoice, customerEmail: email } : w));
  const timeOf = (d) => d ? new Date(d).toLocaleTimeString('en-AU',{hour:'2-digit',minute:'2-digit'}) : '—';

  const th = { padding:'10px 12px', textAlign:'left', fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', borderBottom:'1px solid var(--border)' };
  const td = { padding:'11px 12px', fontSize:'13px', color:'var(--text)', verticalAlign:'middle', borderTop:'1px solid var(--border)' };

  return (
    <div style={T.page}>
      <PageHeader title="Walk-ins" actions={
        <button onClick={()=>navigate('/walkins/new')} style={T.btnPrimary}>
          <i className="ti ti-plus" aria-hidden="true"/> New Walk-in
        </button>
      }/>

      <div style={{ maxWidth:'1150px', margin:'0 auto', padding:'16px 16px 80px' }}>

        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:'10px', marginBottom:'16px' }}>
          {[
            { label:'Total',     value:walkIns.length,  color:'var(--blue)'  },
            { label:'Pending',   value:walkIns.filter(w=>w.status==='pending').length,  color:'var(--amber)' },
            { label:'Ready',     value:walkIns.filter(w=>w.status==='ready').length,    color:'var(--green)' },
            { label:'Revenue',   value:`$${totalRevenue.toFixed(2)}`, color:'var(--green)' },
            { label:'Paid',      value:`$${paidTotal.toFixed(2)}`,    color:'var(--green)' },
          ].map(({label,value,color})=>(
            <div key={label} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px' }}>
              <div style={{ fontSize:'11px', color:'var(--muted)', marginBottom:'6px' }}>{label}</div>
              <div style={{ fontSize:'22px', fontWeight:'500', color }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Date picker */}
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' }}>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{ ...T.input, width:'160px' }}/>
          <span style={{ fontSize:'13px', color:'var(--muted)' }}>{walkIns.length} walk-in{walkIns.length!==1?'s':''}</span>
        </div>

        {/* Online bookings — customers who booked on the website for this day */}
        {bookings.length > 0 && (
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', marginBottom:'12px', overflow:'hidden' }}>
            <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)', fontSize:'13px', fontWeight:'500', color:'var(--text)', display:'flex', alignItems:'center', gap:'8px' }}>
              <i className="ti ti-world" style={{ color:'var(--blue)' }} aria-hidden="true"/>
              Online bookings
              <span style={{ fontSize:'12px', fontWeight:'400', color:'var(--muted)' }}>
                {bookings.filter(b=>!b.walkInId).length} waiting · {bookings.filter(b=>b.walkInId).length} checked in
              </span>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'760px' }}>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} style={{ opacity: b.walkInId ? 0.55 : 1 }}>
                      <td style={{ ...td, width:'70px' }}><span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:'12px', fontWeight:'600', color:'var(--blue)' }}>{b.time || '—'}</span></td>
                      <td style={td}>
                        <div style={{ fontWeight:'500' }}>{b.name || '—'}</div>
                        <div style={{ fontSize:'11px', color:'var(--muted)' }}>{b.phone}</div>
                      </td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontWeight:'500', color:'var(--blue)' }}>{b.rego || '—'}</span></td>
                      <td style={td}>{b.services.map(sv => <div key={sv} style={{ fontSize:'12px' }}>{sv}</div>)}</td>
                      <td style={td}><span style={{ fontSize:'12px', color:'var(--muted)' }}>{b.location}</span></td>
                      <td style={td}>
                        <span style={{ background: b.status==='approved' ? 'var(--green-bg)' : 'var(--amber-bg)', color: b.status==='approved' ? 'var(--green)' : 'var(--amber)', fontSize:'11px', fontWeight:'500', padding:'3px 10px', borderRadius:'20px', whiteSpace:'nowrap' }}>
                          {b.status==='approved' ? 'Confirmed' : b.status==='completed' ? 'Completed' : 'Awaiting confirmation'}
                        </span>
                      </td>
                      <td style={{ ...td, textAlign:'right', whiteSpace:'nowrap' }}>
                        {b.walkInId
                          ? <span style={{ fontSize:'12px', color:'var(--green)' }}><i className="ti ti-circle-check" aria-hidden="true"/> Checked in</span>
                          : <button onClick={()=>navigate('/walkins/new', { state:{ booking:b, date } })} style={{ ...T.btnPrimary, padding:'6px 12px', fontSize:'12px' }}>Check in</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Table */}
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'var(--muted)' }}>Loading...</div>
          ) : walkIns.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center' }}>
              <i className="ti ti-car" style={{ fontSize:'32px', color:'var(--hint)', display:'block', marginBottom:'12px' }} aria-hidden="true"/>
              <div style={{ fontSize:'14px', fontWeight:'500', color:'var(--text)', marginBottom:'6px' }}>No walk-ins today</div>
              <button onClick={()=>navigate('/walkins/new')} style={T.btnPrimary}>Add Walk-in</button>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'900px' }}>
              <thead>
                <tr>
                  {['Customer','Rego','Service','Price','Pickup','Notes','Status','Collected','Paid',''].map(h=>(
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {walkIns.map(w=>{
                  const st = STATUS_MAP[w.status] || STATUS_MAP.pending;
                  return (
                    <tr key={w._id} style={{ transition:'background .1s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={td}>
                        <div style={{ fontWeight:'500' }}>{w.customerName||'—'}</div>
                        <div style={{ fontSize:'11px', color:'var(--muted)' }}>{w.phone||''}</div>
                        {w.invoice && (
                          <div style={{ fontSize:'11px', marginTop:'2px', color: w.invoice.sendStatus==='sent' ? 'var(--green)' : 'var(--red)' }}>
                            <i className="ti ti-file-invoice" aria-hidden="true"/> {w.invoice.number}{w.invoice.sendStatus==='sent' ? ' · sent' : ' · not delivered'}
                          </div>
                        )}
                      </td>
                      <td style={td}><span style={{ fontFamily:'JetBrains Mono,monospace', fontWeight:'500', color:'var(--blue)' }}>{w.rego||'—'}</span></td>
                      <td style={td}>
                        {(w.services?.length ? w.services : [w.service]).map(sv => <div key={sv} style={{ fontSize:'12px' }}>{sv}</div>)}
                      </td>
                      <td style={td}><span style={{ color:'var(--green)', fontWeight:'500' }}>${(w.price||0).toFixed(2)}</span></td>
                      <td style={td}><span style={{ fontSize:'12px', color:'var(--muted)' }}>{w.pickupTime||'—'}</span></td>
                      <td style={{ ...td, maxWidth:'160px' }}><span style={{ fontSize:'12px', color:'var(--muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>{w.notes||'—'}</span></td>
                      <td style={td}>
                        {editingStatus===w._id ? (
                          <select autoFocus value={w.status} onChange={e=>updateStatus(w._id,e.target.value)} onBlur={()=>setEditingStatus(null)}
                            style={{ ...T.input, fontSize:'12px', padding:'4px 8px' }}>
                            {Object.entries(STATUS_MAP).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                          </select>
                        ) : (
                          <span onClick={()=>setEditingStatus(w._id)}
                            style={{ background:st.bg, color:st.color, fontSize:'11px', fontWeight:'500', padding:'3px 10px', borderRadius:'20px', cursor:'pointer', whiteSpace:'nowrap' }}>
                            {w.status==='paid' ? '✓ ' : ''}{st.label} ✎
                          </span>
                        )}
                      </td>
                      <td style={td}>
                        <span style={{ fontSize:'12px', color:'var(--muted)' }}>
                          {timeOf(w.collectedAt)}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={{ fontSize:'12px', color: w.paidAt ? 'var(--green)' : 'var(--muted)' }}>{timeOf(w.paidAt)}</span>
                      </td>
                      <td style={{ ...td, textAlign:'center', whiteSpace:'nowrap' }}>
                        <button onClick={()=>setInvoiceFor(w)} title={w.invoice ? `Invoice ${w.invoice.number}` : 'Send tax invoice'}
                          style={{ background:'transparent', border:'1px solid rgba(10,110,180,0.3)', borderRadius:'var(--radius)', width:'28px', height:'28px', cursor:'pointer', color:'var(--blue)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginRight:'4px' }}>
                          <i className="ti ti-file-invoice" style={{ fontSize:'13px' }} aria-label="Send tax invoice"/>
                        </button>
                        <button onClick={()=>navigate(`/walkins/${w._id}/edit`)} title="Edit"
                          style={{ background:'transparent', border:'1px solid rgba(133,79,11,0.25)', borderRadius:'var(--radius)', width:'28px', height:'28px', cursor:'pointer', color:'var(--amber)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginRight:'4px' }}>
                          <i className="ti ti-edit" style={{ fontSize:'13px' }} aria-label="Edit"/>
                        </button>
                        <button onClick={()=>handleDelete(w._id)}
                          style={{ background:'transparent', border:'1px solid rgba(163,45,45,0.25)', borderRadius:'var(--radius)', width:'28px', height:'28px', cursor:'pointer', color:'var(--red)', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                          <i className="ti ti-trash" style={{ fontSize:'13px' }} aria-label="Delete"/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
      {invoiceFor && (
        <SendInvoiceModal walkIn={invoiceFor} defaultSite={user?.selectedLocation}
          onClose={() => setInvoiceFor(null)} onSent={invoiceSent(invoiceFor._id)}
          onWalkInChanged={(w) => setWalkIns(prev => prev.map(x => x._id===w._id ? { ...x, ...w } : x))}/>
      )}
    </div>
  );
}
